import {
  FC,
  MouseEventHandler,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { ChatMessageReference, FileHistory } from '@crew/models/domain'
import { CrewLoadingText } from 'components/elements/crewLoading/crewLoadingText/crewLoadingText'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { getDefaultTabValue } from '@crew/utils/enum'
import { TaskDetailListTabs } from 'enums/app'
import { CrewTaskHistory } from 'components/elements/crewTaskHistory/crewTaskHistory'
import { EntityType, MessageType, ReferenceType } from '@crew/enums/domain'
import { useGetTaskHistoryQuery } from '@crew/apis/task/taskApis'
import { skipToken } from '@reduxjs/toolkit/query'
import { GetTaskHistoryRequest } from '@crew/apis/task/models/getTaskHistory/request'
import classNames from 'classnames'
import { HTMLEDITOR_VIEW_STYLE } from 'configs/constants'
import { CrewHtmlContent } from 'components/elements/crewHtmlContent/crewHtmlContent'
import { useProjectPermissions, useValueChangeEffect } from '@crew/hooks'
import { isImageFile } from '@crew/utils/chat'
import { CrewAttachmentThumbnails } from 'components/elements//crewAttachmentThumbnails/crewAttachmentThumbnails'
import { CrewAttachments } from 'components/elements/crewAttachments/crewAttachments'
import { sanitizeHtmlAllowNothing } from 'utils/html'
import { useLazyGetFileHistoryQuery } from '@crew/apis/file/fileApis'

export type CrewTaskNotificationChatMessageProps = {
  messageType: MessageType
  chatMessageReferences: ChatMessageReference[]
  truncateMessage: boolean
}

export const CrewTaskNotificationChatMessage: FC<CrewTaskNotificationChatMessageProps> =
  memo((props) => {
    // 現時点では、chat_message_referencesのデータをもとに最新のレコードを取得する仕様
    // このためタスク更新を行った後で「タスク登録時」の自動投稿メッセージには、更新後の件名と説明が表示される（2022/11/10プロダクトオーナー確認済み）

    // 添付ファイル一覧
    const [attachments, setAttachments] = useState<FileHistory[] | undefined>(
      undefined
    )

    // ファイル履歴取得API
    const [lazyGetFileHistoryQuery] = useLazyGetFileHistoryQuery()

    // タスク詳細画面へのリンククリックイベント
    const handleLinkClick = useCallback<MouseEventHandler>((event) => {
      // スレッド表示への切替を防止するため、stopPropagationする
      event.stopPropagation()
      // 画面遷移自体はLinkタグの機能で行われるため、処理を書く必要はない
    }, [])

    // タスクへの参照
    const taskReference = useMemo(() => {
      const reference = props.chatMessageReferences.find((data) => {
        return (
          data.referenceType === ReferenceType.NotificationSource &&
          data.entityType === EntityType.TaskHistories
        )
      })

      // 自動投稿メッセージの場合、必ず1つのデータが存在するはずなので、存在しない場合は異常なデータなので何も処理しない
      if (!reference) {
        return
      }

      return reference
    }, [props.chatMessageReferences])

    // getTaskHistoty params
    const getTaskHistoryParam: GetTaskHistoryRequest | undefined = taskReference
      ? {
          taskHistoryId: taskReference.entityRecordId,
        }
      : undefined
    // Get created message user
    const { data: getTaskHistoryResult } = useGetTaskHistoryQuery(
      getTaskHistoryParam ?? skipToken
    )

    // ファイル履歴への参照
    const fileReferences = useMemo(() => {
      const references = props.chatMessageReferences.filter((data) => {
        return (
          data.referenceType === ReferenceType.NotificationSource &&
          data.entityType === EntityType.FileHistories
        )
      })

      if (!references || references.length === 0) {
        return undefined
      }

      return references
    }, [props.chatMessageReferences])

    // ファイル履歴への参照がある場合、ファイル履歴詳細を取得する
    useValueChangeEffect(
      () => {
        // メッセージの詳細を表示しない場合に添付ファイルを表示しないため、ファイル履歴を取得する必要がない。
        if (props.truncateMessage) {
          return
        }

        // ファイル履歴が存在しない場合は何もしない
        if (!fileReferences) {
          return
        }

        let attachments: FileHistory[] = []

        // 各ファイル履歴の詳細データを取得
        const promises = fileReferences.map(async (reference) => {
          const response = await lazyGetFileHistoryQuery({
            fileHistoryId: reference.entityRecordId,
          }).unwrap()

          // 添付ファイル一覧に追加
          if (response.fileHistory != null) {
            attachments = [...attachments, response.fileHistory]
          }
        })

        // 全ての非同期処理が完了したら、添付ファイル一覧を更新
        Promise.all(promises).then(() => {
          setAttachments(attachments)
        })
      },
      [
        attachments,
        fileReferences,
        lazyGetFileHistoryQuery,
        props.truncateMessage,
      ],
      fileReferences
    )

    // Extract non-image attachments
    const files = useMemo(() => {
      if (!attachments) {
        return
      }

      return attachments.filter((item) => {
        if (!isImageFile(item.name)) {
          return true
        }
        return false
      })
    }, [attachments])

    // Extract attached image files
    const imageFiles = useMemo(() => {
      if (!attachments) {
        return
      }
      return attachments.filter((item) => {
        if (isImageFile(item.name)) {
          return true
        }
        return false
      })
    }, [attachments])

    const descriptionHtml = useMemo(() => {
      switch (props.messageType) {
        case MessageType.TaskCommentUpdated:
          // タスクコメント登録・更新の場合
          return getTaskHistoryResult?.taskComment?.description ?? undefined
        case MessageType.TaskAdded:
        case MessageType.TaskUpdated:
          // タスク登録・更新の場合
          return getTaskHistoryResult?.taskHistory?.description ?? undefined
        default:
          return undefined
      }
    }, [
      getTaskHistoryResult?.taskComment?.description,
      getTaskHistoryResult?.taskHistory?.description,
      props.messageType,
    ])

    const description = useMemo(
      () =>
        descriptionHtml &&
        (props.truncateMessage
          ? sanitizeHtmlAllowNothing(descriptionHtml)
          : descriptionHtml),
      [descriptionHtml, props.truncateMessage]
    )

    // Get permission to download file
    const { hasPrjFileDownloadPermission } = useProjectPermissions(
      EntityType.Task,
      getTaskHistoryResult?.taskHistory?.taskId
    )

    // レンダリング処理
    // 関数化せずに単純にreturn句で描画するコンポーネントを返すだけでも動作はするが、
    // 省略表示かどうかに応じて描画内容が大幅に異なり、可読性が落ちそうに思ったため、レンダリング関数に切り出している
    const renderContent = useCallback(() => {
      // ロードされるまでは表示しない
      if (!getTaskHistoryResult?.taskHistory) {
        return <CrewLoadingText className="w-full h-4" />
      }

      // 再掲トピックで省略表示する場合は、説明+タスク履歴のみを表示する
      if (props.truncateMessage) {
        return (
          <div className="truncate line-clamp-1">
            {/* 説明 */}
            {description && (
              <div className={classNames(HTMLEDITOR_VIEW_STYLE)}>
                <CrewHtmlContent html={description} />
              </div>
            )}

            {/* 履歴 */}
            <CrewTaskHistory taskHistory={getTaskHistoryResult.taskHistory} />
          </div>
        )
      }

      // 再掲トピックでない場合は、タスクの件名、説明、履歴、添付ファイルを表示する
      return (
        <>
          {/** タスクへのリンクを出力 */}
          {/* Linkをインラインとするためのdivタグ（flex-colの対象をこのdivタグにする） */}
          <div>
            <CrewLink
              to={`/tasks/${
                getTaskHistoryResult.taskHistory.taskId
              }/${getDefaultTabValue(TaskDetailListTabs)}`}
              title={getTaskHistoryResult.taskHistory.subject}
              onClick={handleLinkClick}
              className="line-clamp-2"
            >
              {getTaskHistoryResult.taskHistory.subject}
            </CrewLink>
          </div>

          {/* 説明 */}
          {description && (
            <div className={classNames(HTMLEDITOR_VIEW_STYLE, 'line-clamp-5')}>
              <CrewHtmlContent html={description} />
            </div>
          )}

          {/* 履歴 */}
          <CrewTaskHistory taskHistory={getTaskHistoryResult.taskHistory} />

          {/* タスクファイル */}
          {attachments && (
            <div>
              {/* 添付画像ファイル */}
              {imageFiles && (
                <CrewAttachmentThumbnails
                  attachmentThumbnails={imageFiles}
                  showDeleteButton={false}
                  canDownload={hasPrjFileDownloadPermission}
                />
              )}

              {/* 添付ファイル */}
              {files && (
                <CrewAttachments
                  attachments={files}
                  showDeleteButton={false}
                  canDownload={hasPrjFileDownloadPermission}
                />
              )}
            </div>
          )}
        </>
      )
    }, [
      attachments,
      description,
      files,
      getTaskHistoryResult?.taskHistory,
      handleLinkClick,
      hasPrjFileDownloadPermission,
      imageFiles,
      props.truncateMessage,
    ])

    return renderContent()
  })
